import { LiveData, Service } from '@toeverything/infra';
import type { Location } from 'history';
import { Observable } from 'rxjs';

import type { DocsService } from '../../doc';
import type { WorkspaceLocalState } from '../../workspace';

const WORKSPACE_SWITCHER_LIMIT = 5;
// Keep the existing key so current document history migrates in place.
const WORKSPACE_SWITCHER_KEY = 'doc-switcher-mru';

export type WorkspaceSwitcherKind =
  | 'apps'
  | 'chat'
  | 'collection'
  | 'doc'
  | 'documents'
  | 'graph'
  | 'hub'
  | 'journal'
  | 'mail'
  | 'planner'
  | 'settings'
  | 'tag'
  | 'trash'
  | 'workflows';

export interface WorkspaceSwitcherDestination {
  key: string;
  kind: WorkspaceSwitcherKind;
  to: string;
  docId?: string;
  title?: string;
}

const kinds = new Set<WorkspaceSwitcherKind>([
  'apps',
  'chat',
  'collection',
  'doc',
  'documents',
  'graph',
  'hub',
  'journal',
  'mail',
  'planner',
  'settings',
  'tag',
  'trash',
  'workflows',
]);

const routeKinds: ReadonlyArray<{
  root: string;
  kind: Exclude<WorkspaceSwitcherKind, 'doc'>;
}> = [
  { root: '/mail', kind: 'mail' },
  { root: '/planner', kind: 'planner' },
  { root: '/journals', kind: 'journal' },
  { root: '/workflows', kind: 'workflows' },
  { root: '/marketplace', kind: 'hub' },
  { root: '/app-bases', kind: 'apps' },
  { root: '/graph-explorer', kind: 'graph' },
  { root: '/chat', kind: 'chat' },
  { root: '/settings', kind: 'settings' },
  { root: '/trash', kind: 'trash' },
  { root: '/collection', kind: 'collection' },
  { root: '/tag', kind: 'tag' },
  { root: '/all', kind: 'documents' },
];

const isRoute = (pathname: string, root: string) =>
  pathname === root || pathname.startsWith(`${root}/`);

export function toWorkspaceSwitcherDestination(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>
): WorkspaceSwitcherDestination | null {
  const { pathname, search, hash } = location;
  const to = `${pathname}${search}${hash}`;
  const route = routeKinds.find(candidate => isRoute(pathname, candidate.root));

  if (route) {
    return {
      key: `route:${route.kind}`,
      kind: route.kind,
      to,
    };
  }

  const docId = pathname.split('/').filter(Boolean)[0];
  if (!docId) {
    return null;
  }

  return {
    key: `doc:${docId}`,
    kind: 'doc',
    to,
    docId,
  };
}

function normalizeDestinations(value: unknown): WorkspaceSwitcherDestination[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const destinations: WorkspaceSwitcherDestination[] = [];
  for (const item of value) {
    // Previous versions stored bare document IDs.
    if (typeof item === 'string' && item) {
      destinations.push({
        key: `doc:${item}`,
        kind: 'doc',
        to: `/${item}`,
        docId: item,
      });
      continue;
    }

    if (
      typeof item !== 'object' ||
      item === null ||
      !('key' in item) ||
      typeof item.key !== 'string' ||
      !('kind' in item) ||
      typeof item.kind !== 'string' ||
      !kinds.has(item.kind as WorkspaceSwitcherKind) ||
      !('to' in item) ||
      typeof item.to !== 'string'
    ) {
      continue;
    }

    const destination = item as WorkspaceSwitcherDestination;
    destinations.push({
      key: destination.key,
      kind: destination.kind,
      to: destination.to,
      ...(typeof destination.docId === 'string'
        ? { docId: destination.docId }
        : {}),
      ...(typeof destination.title === 'string'
        ? { title: destination.title }
        : {}),
    });
  }
  return destinations.slice(0, WORKSPACE_SWITCHER_LIMIT);
}

export class DocSwitcherService extends Service {
  private readonly pendingTitles = new Map<
    WorkspaceSwitcherKind,
    string | undefined
  >();

  constructor(
    private readonly localState: WorkspaceLocalState,
    private readonly docsService: DocsService
  ) {
    super();
  }

  readonly recentDestinations$ = LiveData.from(
    new Observable<WorkspaceSwitcherDestination[]>(subscriber => {
      subscriber.next(this.getRecentDestinations());
      const sub = this.localState
        .watch<unknown>(WORKSPACE_SWITCHER_KEY)
        .subscribe(value => subscriber.next(normalizeDestinations(value)));
      return () => sub.unsubscribe();
    }),
    []
  );

  readonly availableDestinations$ = LiveData.computed(get => {
    const destinations = get(this.recentDestinations$);
    const docs = get(this.docsService.list.docs$);
    const docIds = new Set(docs.map(doc => doc.id));
    return destinations.filter(
      destination =>
        destination.kind !== 'doc' ||
        (!!destination.docId && docIds.has(destination.docId))
    );
  });

  addLocation(location: Pick<Location, 'pathname' | 'search' | 'hash'>) {
    const destination = toWorkspaceSwitcherDestination(location);
    if (!destination) {
      return;
    }

    const current = this.getRecentDestinations();
    const previous = current.find(item => item.key === destination.key);
    const destinations = current.filter(item => item.key !== destination.key);
    const pendingTitle = this.pendingTitles.get(destination.kind);
    destinations.unshift({
      ...previous,
      ...destination,
      ...(pendingTitle ? { title: pendingTitle } : {}),
    });
    this.localState.set(
      WORKSPACE_SWITCHER_KEY,
      destinations.slice(0, WORKSPACE_SWITCHER_LIMIT)
    );
  }

  updateDestination(
    kind: WorkspaceSwitcherKind,
    patch: Partial<Pick<WorkspaceSwitcherDestination, 'title' | 'to'>>
  ) {
    this.pendingTitles.set(kind, patch.title);
    const current = this.getRecentDestinations();
    const index = current.findIndex(destination => destination.kind === kind);
    if (index < 0) {
      if (!patch.to || kind === 'doc') return;
      this.localState.set(WORKSPACE_SWITCHER_KEY, [
        {
          key: `route:${kind}`,
          kind,
          to: patch.to,
          ...(patch.title ? { title: patch.title } : {}),
        },
        ...current.slice(0, WORKSPACE_SWITCHER_LIMIT - 1),
      ]);
      return;
    }
    const destination = current[index];
    if (!destination) return;
    const next = [...current];
    next[index] = { ...destination, ...patch };
    this.localState.set(WORKSPACE_SWITCHER_KEY, next);
  }

  getResumeTo(kind: WorkspaceSwitcherKind): string | null {
    return (
      this.getRecentDestinations().find(
        destination => destination.kind === kind
      )?.to ?? null
    );
  }

  removeDestination(key: string) {
    const destinations = this.getRecentDestinations().filter(
      destination => destination.key !== key
    );
    this.localState.set(WORKSPACE_SWITCHER_KEY, destinations);
  }

  pruneMissingDocs() {
    const docIds = new Set(
      this.docsService.list.docs$.value.map(doc => doc.id)
    );
    const current = this.getRecentDestinations();
    const next = current.filter(
      destination =>
        destination.kind !== 'doc' ||
        (!!destination.docId && docIds.has(destination.docId))
    );
    if (next.length !== current.length) {
      this.localState.set(WORKSPACE_SWITCHER_KEY, next);
    }
  }

  private getRecentDestinations(): WorkspaceSwitcherDestination[] {
    return normalizeDestinations(this.localState.get(WORKSPACE_SWITCHER_KEY));
  }
}
